/**
 * Validates a Salesforce ID
 *
 * Salesforce IDs are either 15 or 18 characters:
 * - 15-character IDs are case-sensitive base-62 (A-Z, a-z, 0-9)
 * - 18-character IDs append a 3-character case-insensitive suffix
 *   The suffix is calculated based on the case pattern of the 15-char ID
 *   to make the ID case-insensitive
 *
 * Reference: This implementation follows Salesforce's ID validation rules
 * as documented in their API. The case-suffix algorithm ensures case-insensitive
 * uniqueness in file systems that are not case-sensitive.
 *
 * @see https://developer.salesforce.com/docs/atlas.en-us.api.meta/api/field_types.htm#i1435616
 *
 * @param str The string to validate
 * @returns true if the string is a valid 15 or 18 character Salesforce ID
 */
export const isValidSalesforceId = (str: string): boolean => {
  // Base validation - must be string and either 15 or 18 chars
  if (typeof str !== 'string' || (str.length !== 15 && str.length !== 18)) {
    return false;
  }

  // Basic format check - must be alphanumeric
  if (!/^[a-zA-Z0-9]+$/.test(str)) {
    return false;
  }

  // If 15 chars, it's valid as long as it's alphanumeric
  if (str.length === 15) {
    return true;
  }

  // For 18 char IDs, validate the case-insensitive suffix
  const upperCaseToBit = (char: string) => (/[A-Z]/.test(char) ? '1' : '0');
  const binaryToSymbol = (digit: number) =>
    digit <= 25
      ? String.fromCharCode(digit + 65) // A-Z
      : String.fromCharCode(digit - 26 + 48); // 0-9

  const parts = [
    str.slice(0, 5).split('').reverse().map(upperCaseToBit).join(''),
    str.slice(5, 10).split('').reverse().map(upperCaseToBit).join(''),
    str.slice(10, 15).split('').reverse().map(upperCaseToBit).join('')
  ];

  const check = parts.map((binStr) => binaryToSymbol(parseInt(binStr, 2))).join('');
  return check === str.slice(-3);
};
